import React, {useState, forwardRef, JSX} from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaStar, FaHeart, FaShare, FaClock, FaUserFriends, FaCar, FaMicrophoneAlt, FaUndo, FaCreditCard } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons/lib';
import { slovakiaReviews } from '../constants/reviews';
import BokunBookButton from '../components/BokunBookButton';
import { BOKUN_EXPERIENCE } from '../constants/booking';

// Wrappers to fix TypeScript issues with react-icons (ensuring exotic component types, displayName, and casting return for JSX compatibility)
// @ts-ignore
const Star = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaStar {...props} ref={ref} /> as JSX.Element);
Star.displayName = 'Star';

// @ts-ignore
const Heart = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaHeart {...props} ref={ref} /> as JSX.Element);
Heart.displayName = 'Heart';

// @ts-ignore
const Share = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaShare {...props} ref={ref} /> as JSX.Element);
Share.displayName = 'Share';

// @ts-ignore
const Clock = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaClock {...props} ref={ref} /> as JSX.Element);
Clock.displayName = 'Clock';

// @ts-ignore
const UserFriends = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaUserFriends {...props} ref={ref} /> as JSX.Element);
UserFriends.displayName = 'UserFriends';

// @ts-ignore
const Car = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaCar {...props} ref={ref} /> as JSX.Element);
Car.displayName = 'Car';

// @ts-ignore
const MicrophoneAlt = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaMicrophoneAlt {...props} ref={ref} /> as JSX.Element);
MicrophoneAlt.displayName = 'MicrophoneAlt';

// @ts-ignore
const Undo = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaUndo {...props} ref={ref} /> as JSX.Element);
Undo.displayName = 'Undo';

// @ts-ignore
const CreditCard = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaCreditCard {...props} ref={ref} /> as JSX.Element);
CreditCard.displayName = 'CreditCard';

// Styled Components
const HeroContainer = styled.div`
    background: #ffffff;
    padding: 2rem 0 3rem;
`;

const HeroContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const Breadcrumb = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
    color: #666;
    font-size: 0.95rem;

    span {
        cursor: pointer;
        transition: color 0.3s ease;

        &:hover {
            color: #667eea;
        }
    }

    @media (max-width: 768px) {
        font-size: 0.85rem;
        margin-bottom: 1.5rem;
    }
`;

const HeaderSection = styled.div`
    margin-bottom: 3rem;
`;

const TitleSection = styled.div`
    margin-bottom: 2rem;

    h1 {
        font-size: 3rem;
        font-weight: 800;
        margin-bottom: 0.5rem;
        color: #1a1a1a;
        line-height: 1.1;

        @media (max-width: 768px) {
            font-size: 2rem;
            line-height: 1.2;
        }

        @media (max-width: 480px) {
            font-size: 1.75rem;
        }
    }
`;

const Subtitle = styled.p`
    font-size: 1.5rem;
    color: #667eea;
    font-weight: 600;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
        font-size: 1.25rem;
        margin-bottom: 1rem;
    }
`;

const RatingSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
`;

const Stars = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const StarIcon = styled(Star)`
    color: #fbbf24;
    font-size: 1.25rem;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const RatingText = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-weight: 500;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        gap: 0.25rem;
    }
`;

const ReviewCount = styled.span`
    color: #667eea;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const Provider = styled.div`
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 2rem;

    strong {
        color: #667eea;
    }

    @media (max-width: 768px) {
        font-size: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const TitleTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 2rem;

    @media (max-width: 900px) {
        flex-direction: column;
        gap: 1rem;
    }
`;

const TitleHeadings = styled.div`
    flex: 1;
    min-width: 0;
`;

const HeroBookBox = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.5rem;

    @media (max-width: 900px) {
        width: 100%;
    }
`;

const HeroBookButton = styled(BokunBookButton)`
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    font-family: inherit;
    box-sizing: border-box;
    border: none;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.9rem 2.5rem;
    border-radius: 12px;
    font-size: 1.1rem;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
    }

    @media (max-width: 900px) {
        width: 100%;
    }
`;

const Description = styled.p`
    font-size: 1.2rem;
    color: #666;
    line-height: 1.6;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 1.5rem;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;
`;

const IconButton = styled(motion.button)`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: 2px solid #e5e7eb;
    color: #666;
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        border-color: #667eea;
        color: #667eea;
        transform: translateY(-2px);
    }

    svg {
        font-size: 1.1rem;
    }

    @media (max-width: 768px) {
        padding: 0.6rem 1.2rem;
        font-size: 0.9rem;
    }
`;

// Image Gallery - Fixed for mobile
const ImageGallerySection = styled.div`
    margin-bottom: 3rem;
`;

const GalleryContainer = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
    height: 500px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        height: auto;
        gap: 0.75rem;
    }
`;

const MainImage = styled(motion.div)<{ bgImage: string }>`
    background:
            linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)),
            url(${props => props.bgImage}) center/cover;
    border-radius: 16px;
    height: 100%;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    min-height: 300px;

    @media (max-width: 768px) {
        min-height: 250px;
        height: 250px;
        border-radius: 12px;
    }

    @media (max-width: 480px) {
        min-height: 200px;
        height: 200px;
    }
`;

const SideImages = styled.div`
    display: grid;
    grid-template-rows: 1fr 1fr;
    gap: 1rem;
    height: 100%;

    @media (max-width: 768px) {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr;
        height: 150px;
        gap: 0.75rem;
    }

    @media (max-width: 480px) {
        height: 120px;
    }
`;

const SideImage = styled(motion.div)<{ bgImage: string }>`
    background:
            linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)),
            url(${props => props.bgImage}) center/cover;
    border-radius: 12px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    min-height: 150px;

    &:hover {
        transform: scale(1.02);
    }

    @media (max-width: 768px) {
        min-height: 150px;
        height: 100%;
        border-radius: 8px;
    }

    @media (max-width: 480px) {
        min-height: 120px;
    }
`;

const ImageOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    color: white;
    padding: 1rem;
    opacity: 0;
    transition: opacity 0.3s ease;
    font-size: 0.9rem;

    ${MainImage}:hover &, ${SideImage}:hover & {
        opacity: 1;
    }

    @media (max-width: 768px) {
        padding: 0.75rem;
        font-size: 0.8rem;
        opacity: 1; // Always show on mobile for better UX
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    }
`;

const ImageCounter = styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;

    @media (max-width: 768px) {
        top: 0.75rem;
        right: 0.75rem;
        padding: 0.4rem 0.8rem;
        font-size: 0.8rem;
    }
`;

// Activity Section in row layout
const ActivitySection = styled(motion.section)`
    margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 2rem;
    color: #1a1a1a;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: -0.5rem;
        left: 0;
        width: 60px;
        height: 3px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 2px;
    }

    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
    }
`;

const ActivityGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const ActivityItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 1.5rem;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        border-color: #667eea;
    }

    @media (max-width: 768px) {
        padding: 1rem;
        gap: 1rem;
    }
`;

const ActivityIcon = styled.div<{ color?: string }>`
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background: ${props => props.color || '#667eea'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;

    svg {
        font-size: 1.25rem;
    }

    @media (max-width: 768px) {
        width: 40px;
        height: 40px;

        svg {
            font-size: 1rem;
        }
    }
`;

const ActivityContent = styled.div`
    flex: 1;
`;

const ActivityTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #1a1a1a;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        margin-bottom: 0.25rem;
    }
`;

const ActivityDescription = styled.p`
    color: #666;
    line-height: 1.5;
    font-size: 0.95rem;

    @media (max-width: 768px) {
        font-size: 0.85rem;
        line-height: 1.4;
    }
`;

// Modal for Image Gallery
const Modal = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 2rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const ModalImage = styled.img`
    max-width: 90%;
    max-height: 90%;
    border-radius: 12px;
    object-fit: contain;

    @media (max-width: 768px) {
        max-width: 95%;
        max-height: 80%;
    }
`;

const ModalNav = styled.button`
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 2rem;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 1001;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    &.prev {
        left: 5%;
    }

    &.next {
        right: 5%;
    }

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
        font-size: 1.5rem;

        &.prev {
            left: 2%;
        }

        &.next {
            right: 2%;
        }
    }

    @media (max-width: 480px) {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 1.5rem;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 1001;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    @media (max-width: 768px) {
        top: 1rem;
        right: 1rem;
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }
`;

// Main TripDetailHero Component
interface TripDetailHeroProps {
    onNavigateBack: () => void;
}

const TripDetailHero: React.FC<TripDetailHeroProps> = ({ onNavigateBack }) => {
    // Rating summary derived from the real review data so it never goes stale.
    const reviewCount = slovakiaReviews.length;
    const avgRating = reviewCount
        ? (slovakiaReviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount).toFixed(1)
        : '5.0';

    const [selectedImage, setSelectedImage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Updated image paths for the new tour
    const images = [
        '/bratislava-castle.jpg',
        '/img_19.png',
        '/img_20.png',
        '/img_21.png',
        '/img_22.png',
        '/img_23.png',
        '/img_24.png',
        '/img_25.png',
        '/img_26.png'
    ];

    // Fallback images in case the above paths don't work
    const fallbackImages = [
        'https://images.unsplash.com/photo-1592394675778-4239b838fb2c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1590523541541-89b9baf46695?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop'
    ];

    const activityItems = [
        {
            icon: <Undo />,
            title: "Free cancellation",
            description: "Cancel up to 24 hours in advance for a full refund",
            color: "#10b981"
        },
        {
            icon: <CreditCard />,
            title: "Reserve now & pay later",
            description: "Keep your travel plans flexible — book your spot and pay nothing today.",
            color: "#667eea"
        },
        {
            icon: <Clock />,
            title: "Duration 10 hours",
            description: "Check availability to see starting times",
            color: "#f59e0b"
        },
        {
            icon: <MicrophoneAlt />,
            title: "Live tour guide",
            description: "English, Slovak",
            color: "#ef4444"
        },
        {
            icon: <Car />,
            title: "Pickup included",
            description: "Pickup from your hotel or accommodation in Bratislava.",
            color: "#8b5cf6"
        },
        {
            icon: <UserFriends />,
            title: "Small group",
            description: "Limited to 7 participants",
            color: "#06b6d4"
        }
    ];

    const openModal = (index: number) => {
        setSelectedImage(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const navigateImage = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            setSelectedImage(prev => (prev === 0 ? images.length - 1 : prev - 1));
        } else {
            setSelectedImage(prev => (prev === images.length - 1 ? 0 : prev + 1));
        }
    };

    const handleAddToWishlist = () => {
        alert("Added to wishlist!");
    };

    const handleShare = () => {
        alert("Share this tour!");
    };

    // Function to handle image loading errors
    const handleImageError = (e: React.SyntheticEvent<HTMLDivElement, Event>, index: number) => {
        const target = e.target as HTMLDivElement;
        target.style.backgroundImage = `linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)), url(${fallbackImages[index]})`;
    };

    return (
        <HeroContainer>
            <HeroContent>
                {/* Breadcrumb */}
                <Breadcrumb>
                    <span onClick={onNavigateBack}>Home</span>
                    <span>•</span>
                    <span onClick={onNavigateBack}>Tours</span>
                    <span>•</span>
                    <span style={{ color: '#667eea', fontWeight: 600 }}>Slovakia Castles & Čičmany</span>
                </Breadcrumb>

                {/* Header Section */}
                <HeaderSection>
                    <TitleSection>
                        <TitleTopRow>
                            <TitleHeadings>
                                <h1>From Bratislava: Castles, Čičmany & Bratislava Castle</h1>
                                <Subtitle>Historic Castles & Traditional Village</Subtitle>

                                <RatingSection>
                                    <Stars>
                                        <StarIcon />
                                        <StarIcon />
                                        <StarIcon />
                                        <StarIcon />
                                        <StarIcon />
                                        <RatingText>
                                            <strong>{avgRating} out of 5 stars</strong>
                                            <span>•</span>
                                            <ReviewCount onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}>{reviewCount} reviews</ReviewCount>
                                        </RatingText>
                                    </Stars>
                                </RatingSection>

                                <Provider>
                                    Activity provider: <strong>DRL Trips</strong>
                                </Provider>
                            </TitleHeadings>

                            <HeroBookBox>
                                <HeroBookButton experienceId={BOKUN_EXPERIENCE.slovakia}>
                                    Book Now
                                </HeroBookButton>
                            </HeroBookBox>
                        </TitleTopRow>


                        {/* Image Gallery - Fixed for mobile */}
                        <ImageGallerySection>
                            <GalleryContainer>
                                <MainImage
                                    bgImage={images[0]}
                                    onClick={() => openModal(0)}
                                    whileHover={{ scale: 1.01 }}
                                    onError={(e) => handleImageError(e, 0)}
                                >
                                    <ImageOverlay>
                                        Click to view larger
                                    </ImageOverlay>
                                    <ImageCounter>1/{images.length}</ImageCounter>
                                </MainImage>
                                <SideImages>
                                    {images.slice(1, 3).map((image, index) => (
                                        <SideImage
                                            key={index}
                                            bgImage={image}
                                            onClick={() => openModal(index + 1)}
                                            whileHover={{ scale: 1.05 }}
                                            onError={(e) => handleImageError(e, index + 1)}
                                        >
                                            <ImageOverlay>
                                                Click to view larger
                                            </ImageOverlay>
                                            <ImageCounter>{index + 2}/{images.length}</ImageCounter>
                                        </SideImage>
                                    ))}
                                </SideImages>
                            </GalleryContainer>
                        </ImageGallerySection>

                        <Description>
                            Discover Slovakia's rich history on a tour from Bratislava. Visit Červený Kameň, Smolenice and Trenčín Castles, explore the village of Čičmany, Slovak Bethlehem and end at the Bratislava Castle observation deck.
                        </Description>

                    </TitleSection>

                </HeaderSection>

                {/* Activity Section in row layout */}
                <ActivitySection
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <SectionTitle>About this activity</SectionTitle>
                    <ActivityGrid>
                        {activityItems.map((item, index) => (
                            <ActivityItem key={index}>
                                <ActivityIcon color={item.color}>
                                    {item.icon}
                                </ActivityIcon>
                                <ActivityContent>
                                    <ActivityTitle>{item.title}</ActivityTitle>
                                    <ActivityDescription>{item.description}</ActivityDescription>
                                </ActivityContent>
                            </ActivityItem>
                        ))}
                    </ActivityGrid>
                </ActivitySection>

                {/* Image Modal */}
                {isModalOpen && (
                    <Modal
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <CloseButton onClick={closeModal}>
                            ×
                        </CloseButton>
                        <ModalNav className="prev" onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('prev');
                        }}>
                            ‹
                        </ModalNav>
                        <ModalImage
                            src={images[selectedImage]}
                            alt={`Tour image ${selectedImage + 1}`}
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = fallbackImages[selectedImage];
                            }}
                        />
                        <ModalNav className="next" onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('next');
                        }}>
                            ›
                        </ModalNav>
                    </Modal>
                )}
            </HeroContent>
        </HeroContainer>
    );
};

export default TripDetailHero;